/**
 * Base i18n instance for SSR
 *
 * Source: https://github.com/troph-team/pixai/blob/main/packages/pixai-server/src/common/i18n/index.ts
 * This module provides a pre-initialized i18n instance that can be
 * cloned and used for SSR meta generation.
 *
 * Usage in middleware/loader:
 *   import i18n from 'src/i18n/i18n.base'
 *
 *   const i18nInstance = i18n.cloneInstance()
 *   await i18nInstance.changeLanguage(locale)
 */

import i18next from 'i18next'
import ChainedBackend from 'i18next-chained-backend'
import HttpBackend from 'i18next-http-backend'
import resourcesToBackend from 'i18next-resources-to-backend'
import { DEFAULT_LOCALE, LOCALE_CODES, SUPPORTED_LOCALES } from './constants'
import { loadBundledResource } from './resources'
import { simpleLocalizeLoadPath } from './utils'

export const fallbackLng = DEFAULT_LOCALE

// Create and initialize the base i18n instance
const i18n = i18next.createInstance()

/**
 * Dev escape hatch (SSR side): mirrors the client toggle in i18next.ts so
 * server-rendered strings also fall back to bundled JSON when previewing
 * UI with locally-edited keys not yet pushed to SimpleLocalize. Gated by
 * `import.meta.env.DEV` so production SSR builds always go CDN-first.
 */
const localI18nFirst =
  import.meta.env.DEV && import.meta.env.VITE_I18N_LOCAL_FALLBACK === 'true'
const httpBackendOptions = {
  loadPath: simpleLocalizeLoadPath,
  // Reload translations every 60 seconds in server environment
  reloadInterval: typeof window === 'undefined' ? 60_000 : false,
}
const localBackend = resourcesToBackend(loadBundledResource)

/* Source: https://github.com/troph-team/pixai/blob/59aaaea9ee3ef371554a08aa59c095ffe18fdadf/packages/pixai-server/src/common/i18n/index.ts#L77-L95 */
i18n.use(ChainedBackend).init({
  debug: false,
  load: 'languageOnly',
  fallbackLng,
  ns: [
    'server',
    'common',
    'membership',
    'growth',
    'yearrecap',
    'blackboard',
    // `artwork` and `generation` are referenced by route loaders that
    // call `getI18n(context).t('artwork:…')` / `t('generation:…')` —
    // most visibly `_app.market.tsx` (`uploadLabel: t('artwork:upload-local-image')`)
    // and `generator.image.$tab.tsx` (mode-specific titles). Without
    // including them in the SSR namespace list, `cloneInstance().t()`
    // returns `undefined` (gated by `parseMissingKeyHandler`), which
    // becomes empty string by the time it reaches the SSR shell —
    // visibly leaving the Market page's Upload button label blank.
    'artwork',
    'generation',
  ],
  defaultNS: 'server',
  supportedLngs: LOCALE_CODES,
  backend: {
    // CDN primary, bundled JSON fallback. Mirrors the client chain in
    // i18next.ts so an SSR render still produces translated strings if
    // SimpleLocalize / the CDN is degraded.
    backends: localI18nFirst
      ? [localBackend, HttpBackend]
      : [HttpBackend, localBackend],
    backendOptions: localI18nFirst
      ? [{}, httpBackendOptions]
      : [httpBackendOptions, {}],
  },
  returnEmptyString: false,
  showSupportNotice: false,
  parseMissingKeyHandler(_key: string, _defaultValue: any) {},
  interpolation: {
    // Not needed for React as it escapes by default
    escapeValue: false,
  },
})

export default i18n

// Re-export constants for convenience
export { DEFAULT_LOCALE, SUPPORTED_LOCALES }
export type { SupportedLocaleCodes } from './constants'
